import { mutate, mutateUpload } from './api'
import { toast } from './ui'

// The profile picture in the top corner (Liam, 2026-09-23). The phone crops
// the chosen photo to a square and shrinks it to 320px before it is sent, so
// what the server stores is small and what the circle shows is never
// stretched. /api/avatar only ever touches the signed-in person's own picture.

const EDGE = 320
const JPEG_QUALITY = 0.85

// Centre-crop to a square, EDGE px, JPEG. null when the browser cannot read
// the file (HEIC on an old browser, a corrupt file).
export async function squareJpeg(file: File): Promise<Blob | null> {
  if (typeof createImageBitmap !== 'function') return null
  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    try {
      bitmap = await createImageBitmap(file)
    } catch {
      return null
    }
  }
  const side = Math.min(bitmap.width, bitmap.height)
  const sx = Math.round((bitmap.width - side) / 2)
  const sy = Math.round((bitmap.height - side) / 2)
  const out = Math.min(EDGE, side)
  const canvas = document.createElement('canvas')
  canvas.width = out
  canvas.height = out
  const cx = canvas.getContext('2d')
  if (!cx) return null
  cx.drawImage(bitmap, sx, sy, side, side, 0, 0, out, out)
  bitmap.close()
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
}

// Ask for a picture, send it, and reload so every screen shows it. A picture
// that could not be sent is simply not changed: nothing is queued for later,
// because a profile picture is not work that must not be lost.
export function chooseAndUploadAvatar(): void {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.addEventListener('change', async () => {
    const file = input.files?.[0]
    if (!file) return
    const blob = await squareJpeg(file)
    if (!blob) {
      toast("That picture couldn't be read. Try a different one.")
      return
    }
    toast('Saving your picture…')
    const ack = await mutateUpload({
      path: '/api/avatar',
      key: crypto.randomUUID(),
      fields: {},
      file: blob,
      filename: 'picture.jpg',
      fileField: 'image',
      ackField: 'row',
    })
    if (ack.ok) {
      location.reload()
      return
    }
    toast(ack.status === 0 ? "Couldn't save your picture. Check your signal and try again." : `Couldn't save your picture: ${ack.error ?? 'refused'}`)
  })
  input.click()
}

export async function removeAvatar(): Promise<void> {
  const ack = await mutate({ label: 'Remove picture', method: 'DELETE', path: '/api/avatar', body: {} })
  if (ack.ok) {
    location.reload()
    return
  }
  toast("Couldn't remove your picture. Try again when you have signal.")
}
